/**
 * **O balcao** — pedido assistido e as operacoes de turno (`CONTRACTS §8.2` blocos "Painel da
 * Loja" e "Operacao da loja", `§8.3`, `API §6.19` e `§6.20.13`).
 *
 * Sete linhas de rota vivem neste arquivo, e todas tem o mesmo ator: alguem atras do balcao,
 * com o turno aberto numa filial. O que as separa das rotas de `public/` nao e a forma — e
 * quem digita. O balconista **nao e uma excecao ao gate regulatorio** nem ao motor de preco: o
 * `CONTRACTS §8.3` e literal ao dizer *"mesmo motor de preco... e mesma maquina de estados"*, e
 * a consequencia no contrato e visivel aqui — **nao ha campo de dinheiro em nenhuma requisicao
 * deste arquivo**.
 *
 * ## A ausencia que governa o arquivo inteiro
 *
 * `CreateAssistedOrderRequest` tem `items: [{ sku, quantity }]` e nada mais sobre valor.
 * `ApplyAppPriceRequest` tem o **item**, e nao o preco. `SettlePaymentRequest` tem o valor
 * **recebido em maos** — que e dinheiro contado, nao preco cobrado. `CashClosingRequest` tem o
 * valor **contado na gaveta**. Nenhum desses tres entra em cascata de preco nenhuma.
 *
 * `SECURITY §8.1` regra 1 vale igual no balcao: campo de preco vindo de quem digita e recusado
 * pelo `.strict()`. Um `unitPriceCents` aqui seria a porta que o canal do cliente nao tem — e
 * o pedido assistido e *"o fluxo mais usado do ano 1"* (S-01).
 *
 * ## DUVIDAS declaradas (divergencias reportadas)
 *
 * 1. **A "justificativa taxonomizada" da baixa divergente nao tem taxonomia.** `SECURITY`
 *    AMZ-037 exige *"justificativa taxonomizada"* e TST-037 exige `422` sem ela, mas **nenhum
 *    documento enumera a lista**. Aqui o campo e texto livre obrigatorio quando o valor recebido
 *    diverge do total — a obrigatoriedade e o que o teste cobra; a lista fechada entra quando
 *    alguem a escrever. Inventar cinco motivos criaria uma metrica que ninguem pediu.
 * 2. **`GET /v1/store/summary` nao tem schema em documento nenhum.** `API §6.19` o descreve em
 *    prosa: *"Fechamento do dia, cancelamentos por motivo, SLA medio"*. Os tres blocos abaixo
 *    sao exatamente esses tres, e nada alem — nenhuma metrica a mais foi inventada.
 * 3. **`apply-app-price` nao aceita o preco do app no corpo.** `API §6.19` diz que *"o Painel
 *    busca o produto, **mostra** o preco vigente no app naquele momento"* — o preco e do
 *    servidor nos dois lados da tela. O corpo aponta o **item do pedido**; a diferenca e
 *    calculada aqui dentro. Aceitar `appPriceCents` do Painel seria construir, para o balcao, o
 *    caminho de preco que `SECURITY §8.1` fecha para o cliente.
 */
import { z } from 'zod';

import { CancellationReasonSchema, FulfillmentModeSchema, PaymentMethodSchema } from '../enums';
import {
  InstantSchema,
  LocalDateSchema,
  MoneySchema,
  NonNegativeMoneySchema,
  OrderCodeSchema,
  PhoneBrE164Schema,
  QuantitySchema,
  Uuidv7Schema,
} from '../common';
import { OrderStatusSchema } from '../enums';

// ---------------------------------------------------------------------------
// Cache — nenhuma resposta deste arquivo pode ser guardada
// ---------------------------------------------------------------------------

/**
 * `API §6.19`: as rotas do Painel sao `no-store`. `private` acompanha porque todas carregam
 * pedido de titular identificado — e no balcao a tela fica num PC compartilhado (`SECURITY §9`).
 */
export const STORE_COUNTER_CACHE_CONTROL = 'private, no-store';

// ---------------------------------------------------------------------------
// POST /v1/store/orders — pedido assistido (D9, API §6.20.13)
// ---------------------------------------------------------------------------

/**
 * O cliente, identificado por **telefone**.
 *
 * O telefone e a chave porque e o login primario do produto (`customer.phoneE164 @unique`,
 * R-49) e porque e o que o cliente mandou no audio de WhatsApp. `name` so e usado no
 * **pre-cadastro**: se o telefone ja existe, o nome do corpo e ignorado — deixar o balcao
 * renomear um titular existente seria escrita de PII sem pedido do titular.
 */
export const AssistedCustomerSchema = z
  .object({
    phone: PhoneBrE164Schema,
    name: z.string().min(2).max(120).optional(),
  })
  .strict();
export type AssistedCustomer = z.infer<typeof AssistedCustomerSchema>;

/**
 * Uma linha por SKU. **Sem preco** — ver o cabecalho.
 *
 * `sku` e o codigo que o balconista digita ou bipa (`product.erpSku`), nao o UUID: quem esta
 * no balcao tem a caixa na mao, e o teclado e a interface (`CONTRACTS §8.3`, "otimizada para
 * teclado").
 */
export const AssistedOrderItemSchema = z
  .object({
    sku: z.string().min(1).max(64),
    quantity: QuantitySchema,
  })
  .strict();
export type AssistedOrderItem = z.infer<typeof AssistedOrderItemSchema>;

/**
 * A modalidade. Discriminada pelo mesmo motivo de `QuoteFulfillmentRequestSchema`: os ramos
 * exigem coisas diferentes.
 *
 * O ramo `DELIVERY` traz o endereco **por extenso** e nao por id, e a diferenca em relacao ao
 * canal do cliente e o mundo real: o titular do audio de WhatsApp nao tem endereco cadastrado —
 * ele esta ditando a rua para o balconista. `reference` e obrigatorio (R-28: e o que o motoboy
 * usa), e a zona vem escolhida da tabela de frete da filial.
 */
export const AssistedFulfillmentSchema = z.discriminatedUnion('mode', [
  z
    .object({
      mode: z.literal('DELIVERY'),
      zoneId: Uuidv7Schema,
      street: z.string().min(3).max(160),
      number: z.string().max(16).nullable(),
      reference: z.string().min(3).max(160),
    })
    .strict(),
  z.object({ mode: z.literal('PICKUP') }).strict(),
]);
export type AssistedFulfillment = z.infer<typeof AssistedFulfillmentSchema>;

/**
 * O pagamento combinado no telefone. Os tres do MVP (`CONTRACTS §2.3`).
 *
 * `changeForCents` e a **unica** quantia da requisicao inteira, e ela nao e preco: e quanto o
 * cliente vai entregar em dinheiro na porta. Menor que o total ⇒ `CHANGE_AMOUNT_INVALID`
 * (INV-O10) — a mesma regra, na mesma funcao, do canal do cliente.
 */
export const AssistedPaymentSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('PIX') }).strict(),
  z
    .object({
      method: z.literal('DINHEIRO_ENTREGA'),
      changeForCents: z.number().int().nonnegative().nullable(),
    })
    .strict(),
  z
    .object({ method: z.literal('MAQUININHA_ENTREGA'), cardKind: z.enum(['DEBIT', 'CREDIT']) })
    .strict(),
]);
export type AssistedPayment = z.infer<typeof AssistedPaymentSchema>;

/**
 * **O consentimento coletado no balcao** — `ConsentChannel.BALCAO`, com registro
 * (`CONTRACTS §8.3`, `§2.9`).
 *
 * Ele e obrigatorio e nao tem forma "false": `collectedVerbally` e `z.literal(true)`, e um
 * corpo que mandasse `false` e `VALIDATION_ERROR`, nao um pedido sem consentimento. A razao e
 * de desenho de contrato: um booleano de duas pontas convidaria a rota a ter um ramo "sem
 * consentimento, segue mesmo assim", e esse ramo nao pode existir (LGPD art. 7º; AMZ-066).
 *
 * `policyVersion` e a versao do texto que o balconista leu em voz alta — e o que grava
 * `consent_record.policyVersion`, a prova do que a pessoa efetivamente ouviu.
 */
export const CounterConsentSchema = z
  .object({
    policyVersion: z.string().min(1).max(64),
    collectedVerbally: z.literal(true),
  })
  .strict();
export type CounterConsent = z.infer<typeof CounterConsentSchema>;

/**
 * `POST /v1/store/orders` — `API §6.20.13`.
 *
 * `items` tem `.min(1)`: pedido vazio nao e pedido, e recusar na borda evita uma cotacao de
 * cesta vazia (`CART_EMPTY`) que diria a coisa certa pelo caminho mais caro.
 */
export const CreateAssistedOrderRequestSchema = z
  .object({
    customer: AssistedCustomerSchema,
    items: z.array(AssistedOrderItemSchema).min(1).max(50),
    fulfillment: AssistedFulfillmentSchema,
    payment: AssistedPaymentSchema,
    consent: CounterConsentSchema,
    notes: z.string().max(280).optional(),
  })
  .strict();
export type CreateAssistedOrderRequest = z.infer<typeof CreateAssistedOrderRequestSchema>;

// ---------------------------------------------------------------------------
// POST /v1/store/orders/{id}/apply-app-price — S-05
// ---------------------------------------------------------------------------

/**
 * `API §6.19`: honrar no balcao o preco que o cliente viu no app.
 *
 * **O corpo aponta o item, nunca o preco** (DUVIDA 3 do cabecalho). O servidor compara o preco
 * congelado em `order_item.unitPriceCentsSnap` com o preco vigente do app naquela filial e
 * aplica a diferenca — sempre a favor do cliente (CDC art. 30). Diferenca zero ou negativa nao
 * e "honrar preco": e o balcao tentando cobrar mais, e a rota recusa.
 */
export const ApplyAppPriceRequestSchema = z
  .object({
    orderItemId: Uuidv7Schema,
    /** Texto livre para o registro em `audit_log`. O registro em si nao e configuravel. */
    note: z.string().max(280).optional(),
  })
  .strict();
export type ApplyAppPriceRequest = z.infer<typeof ApplyAppPriceRequestSchema>;

export const ApplyAppPriceResponseSchema = z
  .object({
    orderId: Uuidv7Schema,
    orderItemId: Uuidv7Schema,
    /** Base do `ETag` fraco `W/"order-<id>-<version>"` (`API §4.2`). */
    version: z.number().int().nonnegative(),
    /** Quanto saiu do total, em favor do cliente. Sempre positivo. */
    difference: NonNegativeMoneySchema,
    /** O total do pedido depois da diferenca. */
    total: NonNegativeMoneySchema,
    /** `true` quando a diferenca passou de `counter.honorAppPriceLimitCents` e exigiu gerente. */
    requiredManager: z.boolean(),
    /** O `audit_log.id` gravado. E o unico controle que a rede tem sobre desconto de balcao. */
    auditEventId: z.string().min(1),
  })
  .strict();
export type ApplyAppPriceResponse = z.infer<typeof ApplyAppPriceResponseSchema>;

// ---------------------------------------------------------------------------
// POST /v1/store/pause | /resume — P7, API §6.5
// ---------------------------------------------------------------------------

/**
 * `API §6.5`: *"Pausa 15/30/60 min (P7)"*. Lista fechada e nao numero livre — "modo ocupado"
 * e um botao com tres opcoes na tela do balcao, e um inteiro arbitrario transformaria a pausa
 * operacional numa forma de fechar a loja sem fechar a loja.
 */
export const STORE_PAUSE_MINUTES_VALUES = [15, 30, 60] as const;
export const StorePauseMinutesSchema = z.union([z.literal(15), z.literal(30), z.literal(60)]);
export type StorePauseMinutes = z.infer<typeof StorePauseMinutesSchema>;

/**
 * A filial vem no **corpo**, e nao num header: `API §1.2` recusa `X-Store-Id` com todas as
 * letras. Ela e conferida contra o escopo do turno — filial fora do vinculo e `404`.
 */
export const StorePauseRequestSchema = z
  .object({
    storeId: Uuidv7Schema,
    minutes: StorePauseMinutesSchema,
    /** Motivo visivel no Painel e no `audit_log`. Pausa sem motivo vira habito. */
    reason: z.string().min(3).max(160),
  })
  .strict();
export type StorePauseRequest = z.infer<typeof StorePauseRequestSchema>;

export const StoreResumeRequestSchema = z.object({ storeId: Uuidv7Schema }).strict();
export type StoreResumeRequest = z.infer<typeof StoreResumeRequestSchema>;

/** A mesma forma das duas rotas: o estado resultante da filial. */
export const StorePauseStateSchema = z
  .object({
    storeId: Uuidv7Schema,
    /** `false` enquanto `pausedUntil` estiver no futuro. */
    acceptingOrders: z.boolean(),
    pausedUntil: InstantSchema.nullable(),
    reason: z.string().nullable(),
  })
  .strict();
export type StorePauseState = z.infer<typeof StorePauseStateSchema>;

// ---------------------------------------------------------------------------
// POST /v1/store/pickups/validate — API §6.14
// ---------------------------------------------------------------------------

/**
 * O codigo de retirada: **6 caracteres** (`CONTRACTS §10.3` — o formato nao e configuravel).
 *
 * Alfanumerico em caixa alta porque ele e **lido em voz alta no balcao**; a normalizacao de
 * caixa e do servidor, e nao do balconista com pressa.
 */
export const PickupCodeSchema = z
  .string()
  .min(6)
  .max(16)
  // Espaco e hifen caem **antes** da validacao: o cliente dita "a-b-c 2 3 4" e o balconista
  // digita como ouviu. Recusar o codigo certo por causa de um hifen ensina o balcao a pular a
  // conferencia — que e o oposto do que a rota existe para fazer.
  .transform((valor) => valor.replace(/[\s-]/gu, '').toUpperCase())
  .pipe(z.string().regex(/^[0-9A-Z]{6}$/, 'O código de retirada tem 6 caracteres.'));
export type PickupCode = z.infer<typeof PickupCodeSchema>;

export const ValidatePickupRequestSchema = z
  .object({
    storeId: Uuidv7Schema,
    code: PickupCodeSchema,
  })
  .strict();
export type ValidatePickupRequest = z.infer<typeof ValidatePickupRequestSchema>;

/**
 * `{ code }` -> pedido (`API §6.14`).
 *
 * O que volta e o **minimo para o balconista entregar a sacola**: qual pedido, em que estado,
 * quantos volumes e ate quando a guarda vale. **Nao volta item** — nome de medicamento numa
 * resposta de validacao de codigo e PHI numa tela que fica aberta no balcao (REG-044); a lista
 * de itens ja esta na fila do Painel, atras da rota que a autoriza.
 */
export const ValidatePickupResponseSchema = z
  .object({
    orderId: Uuidv7Schema,
    code: OrderCodeSchema,
    status: OrderStatusSchema,
    itemCount: z.number().int().nonnegative(),
    total: NonNegativeMoneySchema,
    pickupExpiresAt: InstantSchema.nullable(),
    /** `true` quando ha medicamento na sacola: o balconista chama o farmaceutico (REG-005). */
    requiresPharmacist: z.boolean(),
  })
  .strict();
export type ValidatePickupResponse = z.infer<typeof ValidatePickupResponseSchema>;

// ---------------------------------------------------------------------------
// POST /v1/store/orders/{id}/payments/settle — API §6.13
// ---------------------------------------------------------------------------

/**
 * *"Balconista/entregador marca pagamento na entrega como recebido, com valor efetivo.
 * Divergencia exige justificativa"* (`API §6.13`; AMZ-037; TST-037 pede `422` sem ela).
 *
 * `receivedCents` e dinheiro **contado**, nao preco. Ele nunca reescreve `order.totalCents`:
 * ele grava `payment.paidAt` e alimenta o esperado do fechamento de caixa. Ver a DUVIDA 1 do
 * cabecalho sobre a taxonomia que o documento pede e nao enumera.
 */
export const SettlePaymentRequestSchema = z
  .object({
    receivedCents: z.number().int().nonnegative(),
    method: PaymentMethodSchema,
    justification: z.string().min(3).max(280).optional(),
  })
  .strict();
export type SettlePaymentRequest = z.infer<typeof SettlePaymentRequestSchema>;

export const SettlePaymentResponseSchema = z
  .object({
    orderId: Uuidv7Schema,
    paymentId: Uuidv7Schema,
    method: PaymentMethodSchema,
    status: z.string().min(1),
    settledAt: InstantSchema,
    received: NonNegativeMoneySchema,
    expected: NonNegativeMoneySchema,
    /** `received - expected`. Negativo = faltou dinheiro na sacola. */
    difference: MoneySchema,
  })
  .strict();
export type SettlePaymentResponse = z.infer<typeof SettlePaymentResponseSchema>;

// ---------------------------------------------------------------------------
// POST /v1/store/cash-closing — P9, API §6.13
// ---------------------------------------------------------------------------

/**
 * *"Fechamento de caixa por meio de pagamento e por entregador (P9)"*.
 *
 * O corpo traz o que foi **contado**; o esperado e do servidor (soma dos pagamentos do
 * periodo). Aceitar o esperado do Painel seria deixar quem conta declarar quanto deveria ter.
 */
export const CashClosingRequestSchema = z
  .object({
    storeId: Uuidv7Schema,
    countedCashCents: z.number().int().nonnegative(),
    countedCardCents: z.number().int().nonnegative(),
    /** Caixa por entregador. `null` = caixa da filial no turno (o caso do MVP). */
    courierId: Uuidv7Schema.nullable().optional(),
    notes: z.string().max(280).optional(),
  })
  .strict();
export type CashClosingRequest = z.infer<typeof CashClosingRequestSchema>;

/** `cash_session.status`: `ABERTA | FECHADA | DIVERGENTE` (DATA_MODEL). */
export const CASH_SESSION_STATUS_VALUES = ['ABERTA', 'FECHADA', 'DIVERGENTE'] as const;
export const CashSessionStatusSchema = z.enum(CASH_SESSION_STATUS_VALUES);
export type CashSessionStatus = z.infer<typeof CashSessionStatusSchema>;

export const CashClosingResponseSchema = z
  .object({
    sessionId: Uuidv7Schema,
    storeId: Uuidv7Schema,
    courierId: Uuidv7Schema.nullable(),
    openedAt: InstantSchema,
    closedAt: InstantSchema,
    expectedCash: NonNegativeMoneySchema,
    countedCash: NonNegativeMoneySchema,
    expectedCard: NonNegativeMoneySchema,
    countedCard: NonNegativeMoneySchema,
    /** `contado - esperado`, somando as duas formas. Negativo = falta. */
    difference: MoneySchema,
    status: CashSessionStatusSchema,
  })
  .strict();
export type CashClosingResponse = z.infer<typeof CashClosingResponseSchema>;

// ---------------------------------------------------------------------------
// GET /v1/store/summary — API §6.19
// ---------------------------------------------------------------------------

/** `?storeId=&date=` — a data e local (fuso da instancia), nao instante. */
export const StoreSummaryQuerySchema = z
  .object({
    storeId: Uuidv7Schema,
    date: LocalDateSchema.optional(),
  })
  .strict();
export type StoreSummaryQuery = z.infer<typeof StoreSummaryQuerySchema>;

export const StoreSummaryResponseSchema = z
  .object({
    storeId: Uuidv7Schema,
    date: LocalDateSchema,
    /** Bloco 1 de `API §6.19` — "fechamento do dia". */
    orders: z
      .object({
        placed: z.number().int().nonnegative(),
        delivered: z.number().int().nonnegative(),
        cancelled: z.number().int().nonnegative(),
        /** Quanto do dia veio do balcao (D9). E o numero que justifica a fase F4. */
        assisted: z.number().int().nonnegative(),
        revenue: NonNegativeMoneySchema,
      })
      .strict(),
    /** Bloco 2 — "cancelamentos por motivo", pela lista fechada de `CONTRACTS §2.1`. */
    cancellationsByReason: z.array(
      z.object({ reason: CancellationReasonSchema, count: z.number().int().positive() }).strict(),
    ),
    /** Bloco 3 — "SLA medio" de aceite, e quantos estouraram. */
    acceptanceSla: z
      .object({
        averageSeconds: z.number().int().nonnegative().nullable(),
        breachedCount: z.number().int().nonnegative(),
      })
      .strict(),
    byFulfillmentMode: z.array(
      z
        .object({
          mode: FulfillmentModeSchema,
          count: z.number().int().nonnegative(),
          revenue: NonNegativeMoneySchema,
        })
        .strict(),
    ),
    byPaymentMethod: z.array(
      z
        .object({
          method: PaymentMethodSchema,
          count: z.number().int().nonnegative(),
          revenue: NonNegativeMoneySchema,
        })
        .strict(),
    ),
  })
  .strict();
export type StoreSummaryResponse = z.infer<typeof StoreSummaryResponseSchema>;
